/**
 * Program presets — starting points an administrator edits, not settings.
 *
 * ## What a preset is
 *
 * The same thing an assessment preset is (`assessment-presets.ts`): a template
 * whose values are **copied** into a `programs/{id}` document and then belong to
 * the school. A preset is never a live link. Editing one here does not change a
 * policy anybody is running, which matters more for programs than for
 * assessments — a silent change to a criterion is a silent change to who is
 * allowed to leave the building.
 *
 * So nothing in this file is read at evaluation time. It exists so that the
 * first thing an administrator sees in the policy editor is their own policy,
 * already filled in, with every number obviously editable — rather than an empty
 * form and a specification to retype.
 *
 * ## Why two of them
 *
 * One preset proves nothing. Senior privileges is measures, a box and holds;
 * athletic eligibility is a grade floor over some departments, kept for a
 * hand-maintained roster. Athletics did need vocabulary — a department filter
 * and a roster population — but each is a word a third program can use, not a
 * screen for this one. That is the argument for having
 * built a document system instead of two bespoke screens.
 *
 * ## Source of the senior privileges values
 *
 * "Senior Off-Campus Privilege Policy 2026-2027", draft. Eligibility clauses:
 *
 * > * All overall grades are B- or better.
 * > * Strong daily attendance (no more than 1 unexcused absence or 2 tardies
 * >   per 2 weeks).
 * > * Strong conduct, following school policy (including cell phones, hall
 * >   passes, and parking permits).
 * > * Starting semester 2: all semester 1 classes passed.
 * > * Students return/arrive on time for the next class when going off-campus
 * >   or arriving late.
 *
 * plus, from the application: parent/guardian written permission, proof of
 * family medical and health insurance, and an advisor endorsement.
 *
 * ## Why that is one checkbox and not four
 *
 * Those three are all signatures on the same piece of paper. The office does
 * not receive an insurance proof on Tuesday and an advisor endorsement on
 * Friday; it receives a completed form or it does not. Four boxes would mean
 * four clicks per student for a single physical event, and would invite the
 * state nobody wants — three ticked and one forgotten, so the student is
 * refused at the desk over a box rather than over anything real.
 *
 * Administrator approval is not a box either. The policy grants placement and
 * removal "at the sole discretion of the High School Administration", and
 * removal is already expressible as a hold — which is strictly better than a
 * checkbox, because it carries a reason and an end date. An approval gate that
 * is always ticked for everyone who returned a form is a step, not a control.
 *
 * If the school does want them separated, that is four flags and four criteria
 * in the editor and no deploy. Being wrong about this cheaply is the point.
 *
 * ## Three readings the written policy left open, and how they were settled
 *
 * Every one is a stored parameter, so each stays a document edit rather than a
 * deploy. The reasoning is recorded because it is what a future reader wants.
 *
 * - **"per 2 weeks" is a rolling 14 calendar days**, not fixed fortnightly
 *   buckets. Rolling is what lets a student earn the privilege back with two
 *   clean weeks; buckets would hold a Monday slip against them until an
 *   arbitrary boundary passed.
 * - **Both absences and tardies count unexcused only.** The policy's wording —
 *   "1 unexcused absence or 2 tardies" — qualifies the first and not the
 *   second; that was an oversight, and an excused tardy is excused. This also
 *   keeps the bus cohort (reliably excused-late every morning) from losing a
 *   privilege over the school's own transport problem.
 * - **"B- or better" is the 2.7 cut point** the app already uses for
 *   `below_b_courses`. Note it is *not* the same set as "anything yellow in
 *   Updraft", which is 1.8-2.5 and excludes failing courses; a 2.6 is below a
 *   B- and renders green. That mismatch may be a bug in the coloring rather
 *   than here — see `docs/senior-privileges.md` §8.
 *
 * ## The semester-2 clause, and the rule that made it simple
 *
 * > Starting semester 2: all semester 1 classes passed.
 *
 * Expressed as `transcript_below` with `term=S1` and a 1.8 floor, effective
 * from the first day of semester 2. The rule is read as **"no grade from that
 * term is failing"**, which is vacuously true on an empty transcript — if no
 * grade exists, no grade is failing.
 *
 * That reading is what makes it shippable. Semester 2 does not begin when the
 * transcript is ready: teachers get time to finalize, so for the first weeks of
 * S2 there are legitimately no S1 transcript grades. A rule that treated that
 * silence as unanswerable would flip every senior to a non-answer in January
 * for a reason no student could act on.
 *
 * The gap this leaves is real, small and accepted: right at the start of S2 a
 * student who did badly in S1 keeps the privilege until their transcript
 * catches up. In practice the rolling current-grade criterion covers much of
 * it, because in the first days of S2 `recentGrades` may still hold S1 grades —
 * and a student who was struggling then is rarely thriving on day three.
 *
 * `effective_from` is 2027-01-19, the HS S2 start confirmed by
 * `npm run survey:terms` against production on 2026-09-19. It is a fact about
 * one calendar, so re-check it each year rather than trusting the constant.
 */
import { DEFAULT_PROGRAM_ICON, programId } from "./programs.js";
import { programMeasureId } from "./programMeasures.js";
import type { Program } from "./programs.js";

/**
 * A preset is everything about a program except which one it is.
 *
 * `id` and `school_year` are supplied at creation — a new school year is a new
 * document, as honors does it — and `edits` starts empty because the trail
 * belongs to the school's copy, not to the template.
 */
export type ProgramPreset = Omit<
  Program,
  "id" | "slug" | "school_year" | "edits"
> & {
  /** Shown beside the preset when offering it. */
  note?: string;
};

/** The 2.7 cut point, matching the webapp's `B_MINUS_CUTOFF`. */
export const B_MINUS = 2.7;

/**
 * First day of HS semester 2, 2026-2027, from `lookups/marking_periods`.
 *
 * Recorded so the semester-2 clause has a real date to use when its measure
 * family exists — not so anything reads it at runtime. Anything evaluated at a
 * term boundary should resolve the term from the lookup rather than trust a
 * constant checked in a year earlier.
 */
export const SEMESTER_2_START_HS = "2027-01-19";

export const SENIOR_PRIVILEGES_PRESET: ProgramPreset = {
  name: "Senior off-campus privilege",
  school_id: "HS",
  note: "Prefilled from the 2026-2027 draft policy. Every threshold, window and sentence below is editable.",
  // Seeded off. Turning a program on is a decision a person makes after
  // reading what it would say about real students.
  enabled: false,
  icon: "senior-privileges",
  population: { grade_level: [12], school_id: "HS" },
  criteria: [
    {
      id: "c_form",
      kind: "flag",
      flag: "form_on_file",
      label: "Senior privileges form on file",
      short_label: "SP Form",
      write_roles: ["office"],
      detail:
        "The completed application: student signature, parent/guardian signature, proof of health insurance, and the advisor endorsement.",
    },
    {
      id: "c_conduct",
      short_label: "Conduct",
      kind: "no_hold",
      label: "No conduct hold",
      // The prose said "an advisor or administrator" from the first draft; the
      // code only let administrators near a hold. A hold is a criterion like
      // any other, so it names its roles like any other.
      write_roles: ["advisor", "administrator"],
      detail:
        "Set by an advisor or administrator. Covers school policy generally, including cell phones, hall passes, parking permits, and returning to class on time.",
    },
    {
      id: "c_grades",
      short_label: "Grades",
      kind: "measure",
      measure: "grades_below:threshold=2.7",
      op: "<=",
      value: 0,
      label: "All grades B- or better",
    },
    {
      id: "c_absences",
      short_label: "Absences",
      kind: "measure",
      measure:
        "attendance_count:kind=absent;n=14;scope=daily;status=unexcused;window=calendar_days",
      op: "<=",
      value: 1,
      label: "No more than 1 unexcused absence in the last two weeks",
    },
    {
      id: "c_s1_passed",
      short_label: "S1 Grades",
      kind: "measure",
      // 1.8 is a C- on the transcript scale; the school marks D+ and below as
      // earning no credit, so "below 1.8" is "did not pass".
      measure: "transcript_below:term=S1;threshold=1.8",
      op: "<=",
      value: 0,
      label: "All semester 1 classes passed",
      effective_from: SEMESTER_2_START_HS,
      detail:
        "Checked against finalized semester 1 grades. A student whose transcript has not been published yet is not failing anything, so this passes until a failing grade appears.",
    },
    {
      id: "c_tardies",
      short_label: "Tardies",
      kind: "measure",
      measure:
        "attendance_count:kind=tardy;n=14;scope=daily;status=unexcused;window=calendar_days",
      op: "<=",
      value: 2,
      label: "No more than 2 unexcused tardies in the last two weeks",
    },
  ],
};

/**
 * The departments athletics counts as "core academic", as `lookups/sections`
 * spells them. Checked against a real section export (2025-2026): the MS core
 * is exactly these four — ELA, Math, Science Project and Social Studies
 * Project.
 */
export const CORE_DEPARTMENTS_MS = [
  "English",
  "Math",
  "Science",
  "Social Studies",
] as const;

/**
 * The HS core adds World Languages — "we do check Spanish" — and leaves out,
 * besides the PE, Music and Academic Support the rule names, Art, Health and
 * Wellness, Interdisciplinary (internships, senior project) and the "N/A"
 * sections (advisory, guided study). Those last four are a reading of "only
 * core academic classes" rather than something athletics said, and each is a
 * checkbox in the editor if the reading is wrong.
 */
export const CORE_DEPARTMENTS_HS = [...CORE_DEPARTMENTS_MS, "World Languages"];

/** C-, the athletics standard. Also where `gradeToStatus` puts failing. */
export const ATHLETIC_FLOOR = 1.8;

/**
 * Athletic eligibility — the athletics office's own rules, as data.
 *
 * ## The rule
 *
 * **Every core class at 1.8 or better.** One class under 1.8 and the student
 * does not play. The same at both schools; the schools differ only in which
 * classes are core.
 *
 * The first email said "maintain an average of 1.8", and it meant *in each
 * class*. "Average" is a leftover from when the middle school reported strands
 * — several grades per class — and a class's strands were averaged. Current
 * grades are one number per class, so there is nothing to average: a 0.5 in
 * English is ineligible whatever Science says.
 *
 * Two programs rather than one, because the schools count different classes
 * and have different teams, and a program names one school.
 *
 * ## The roster is kept by hand
 *
 * Aspen does not export teams and never will. So the population is a roster the
 * athletics office maintains on the program itself — pasted in from the list
 * coaches already send, grouped by team — which is the `roster` population, not
 * a whole school evaluated in case somebody plays. Every student read the
 * evaluator does is an athlete.
 *
 * ## Warnings are exceptions
 *
 * In practice athletics warns before it benches. That is an **exception** on
 * the grade rule, granted by the athletics office until a date, with a reason:
 * the student reads eligible in amber while it runs, and goes back to red the
 * day after it ends, with the record of what was granted and by whom kept. No
 * warning rule is needed; the rule stays "under 1.8 is no", and the exception
 * is a person deciding otherwise for a fortnight.
 *
 * ## The grace period is a waiver
 *
 * The other thing athletics does is say "let them play this week regardless"
 * — a new student whose grades have not settled, a schedule change that left
 * a class at zero. That is not an excuse for one rule; it is a yes for the
 * whole program, so it is a `waiver` criterion rather than another exception.
 * It sits first, so the column the office acts on most is the first one read.
 * A hold still beats it: the office benching a student overrides the office
 * having waved them through.
 *
 * Labelled "Grace period" because that is a reasonable first guess at what the
 * office calls it, and only a guess — labels are data, edit them in the policy.
 */
function athleticCriteria(
  departments: readonly string[],
  schoolWord: string,
): ProgramPreset["criteria"] {
  const dept = [...departments];
  return [
    {
      id: "c_waiver",
      short_label: "Grace period",
      kind: "waiver",
      label: "On a grace period from the athletics office",
      write_roles: ["office"],
      detail:
        "Eligible regardless of the other rules until the date set, unless a hold is placed. One week is typical.",
    },
    {
      id: "c_grades",
      short_label: "Core classes",
      kind: "measure",
      measure: programMeasureId({
        family: "grades_below",
        params: { threshold: ATHLETIC_FLOOR, departments: dept },
      }),
      op: "<=",
      value: 0,
      label: `1.8 (C-) or better in every ${schoolWord} core class`,
      detail: `Counts ${dept.join(", ")}. A student with no grade posted in a class has nothing against them in it.`,
      // Exceptions are how the warning before a benching is given, and the
      // athletics office gives them.
      allow_exceptions: true,
      write_roles: ["office"],
    },
    {
      id: "c_hold",
      short_label: "Hold",
      kind: "no_hold",
      label: "No eligibility hold",
      write_roles: ["office"],
      detail: "Set by the athletics office or an administrator.",
    },
  ];
}

export const ATHLETIC_ELIGIBILITY_MS_PRESET: ProgramPreset = {
  name: "Athletic eligibility (MS)",
  school_id: "MS",
  enabled: false,
  icon: "athletic-eligibility",
  status_label: "Eligible",
  note: "The athletics office's middle school rule: 1.8 or better in every core class. Add athletes on the program's page; teams are not in Aspen.",
  population: { school_id: "MS", roster: { group_label: "Team" } },
  criteria: athleticCriteria(CORE_DEPARTMENTS_MS, "MS"),
};

export const ATHLETIC_ELIGIBILITY_HS_PRESET: ProgramPreset = {
  name: "Athletic eligibility (HS)",
  school_id: "HS",
  enabled: false,
  icon: "athletic-eligibility",
  status_label: "Eligible",
  note: "The athletics office's high school rule: 1.8 or better in every core class, World Languages included. Add athletes on the program's page; teams are not in Aspen.",
  population: { school_id: "HS", roster: { group_label: "Team" } },
  criteria: athleticCriteria(CORE_DEPARTMENTS_HS, "HS"),
};

/** Presets offered when creating a program. */
export const PROGRAM_PRESETS: Record<string, ProgramPreset> = {
  senior_privileges: SENIOR_PRIVILEGES_PRESET,
  athletic_eligibility: ATHLETIC_ELIGIBILITY_MS_PRESET,
  athletic_eligibility_hs: ATHLETIC_ELIGIBILITY_HS_PRESET,
};

export function presetFor(key: string): ProgramPreset | null {
  return PROGRAM_PRESETS[key] ?? null;
}

/** An empty program, for a privilege nobody has templated. */
export function blankPreset(name: string): ProgramPreset {
  return {
    name,
    school_id: "HS",
    enabled: false,
    icon: DEFAULT_PROGRAM_ICON,
    population: {},
    criteria: [],
  };
}

/**
 * Turns a preset into a real policy document.
 *
 * The copy is deep for `criteria` so that editing the new program cannot reach
 * back into the module-level template and change what the *next* program starts
 * from — a bug that would only show up on the second use.
 */
export function programFromPreset(
  preset: ProgramPreset,
  slug: string,
  schoolYear: string,
): Program {
  return {
    ...preset,
    slug,
    id: programId(slug, schoolYear),
    school_year: schoolYear,
    population: {
      ...preset.population,
      ...(preset.population.roster ? { roster: { ...preset.population.roster } } : {}),
    },
    criteria: preset.criteria.map((criterion) => ({ ...criterion })),
    edits: [],
  };
}
